/**
 * StaggeredList — Renders children with staggered entrance animation.
 *
 * Each child appears with a delay offset from the previous.
 * Capped at 6 visible stagger items (additional items appear simultaneously).
 *
 * @see documents/ui-enhancement-spec.md §9.3 (Card & Content Entrances)
 */

import React, { useEffect, useState } from 'react';
import { useZeliumTheme } from '../../theme';

interface StaggeredListProps {
  children: React.ReactNode;
  /** Delay between each item in seconds (default: 0.08) */
  staggerDelay?: number;
  /** Maximum items to stagger (default: 6) */
  maxStagger?: number;
  /** Base delay before first item appears in ms (default: 0) */
  baseDelay?: number;
  /**
   * Gap between items as a Polaris spacing token (default: "400" = 16px).
   * Accepts any Polaris space token: "100" | "200" | "300" | "400" | "500" | "600" | "800"
   */
  gap?: string;
}

export function StaggeredList({
  children,
  staggerDelay,
  maxStagger = 6,
  baseDelay = 0,
  gap = '400',
}: StaggeredListProps): React.ReactElement {
  const { reducedMotion, motion } = useZeliumTheme();
  const items = React.Children.toArray(children);
  const effectiveStagger = staggerDelay ?? motion.stagger.default;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `var(--p-space-${gap})` }}>
      {items.map((child, index) => (
        <StaggeredItem
          key={index}
          index={index}
          staggerDelay={effectiveStagger}
          maxStagger={maxStagger}
          baseDelay={baseDelay}
          reducedMotion={reducedMotion}
          duration={motion.duration.fast}
          ease={motion.ease.enter}
        >
          {child}
        </StaggeredItem>
      ))}
    </div>
  );
}

interface StaggeredItemProps {
  children: React.ReactNode;
  index: number;
  staggerDelay: number;
  maxStagger: number;
  baseDelay: number;
  reducedMotion: boolean;
  duration: number;
  ease: string;
}

function StaggeredItem({
  children,
  index,
  staggerDelay,
  maxStagger,
  baseDelay,
  reducedMotion,
  duration,
  ease,
}: StaggeredItemProps): React.ReactElement {
  const [visible, setVisible] = useState(reducedMotion);

  const delay = index < maxStagger
    ? baseDelay + index * staggerDelay * 1000
    : baseDelay + maxStagger * staggerDelay * 1000;

  useEffect(() => {
    if (reducedMotion) {
      setVisible(true);
      return;
    }
    const timeout = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timeout);
  }, [delay, reducedMotion]);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(12px)',
    transition: `opacity ${duration}s ${ease}, transform ${duration}s ${ease}`,
  };

  return <div style={style}>{children}</div>;
}
